export interface CompareArraysOptions<TLeft, TRight> {
    left: TLeft[];
    right: TRight[];
    compare: (left: TLeft, right: TRight) => number;
}

export interface CompareArrayOptionsWithEvents<TLeft, TRight> extends CompareArraysOptions<TLeft, TRight> {
    missingInLeft?: (right: TRight) => void;
    missingInRight?: (left: TLeft) => void;
    matched?: (left: TLeft, right: TRight) => void;
}

/**
 * Compares 2 arrays, both in ascending order, and reports back on missing and matched items.
 * Note that this function will not work correctly unless the arrays you provide are both in ascending order.
 * Implementation deliberately avoids adding automatic sorting/checking for performance, so its up to the
 * caller to get it right.
 *
 * @param options - options to compare arrays
 */
// eslint-disable-next-line max-statements
export function compareArrays<TLeft, TRight>(options: CompareArrayOptionsWithEvents<TLeft, TRight>): void {
    const { compare, left, matched, missingInLeft, missingInRight, right } = options;

    const leftLength = left.length;
    const rightLength = right.length;

    let leftIndex = 0;
    let rightIndex = 0;

    const leftFinished = () => leftIndex >= leftLength;
    const rightFinished = () => rightIndex >= rightLength;

    while (!leftFinished() || !rightFinished()) {
        const leftItem = left[leftIndex];
        const rightItem = right[rightIndex];


        const result = leftFinished() ? 1 : rightFinished() ? -1 : compare(leftItem, rightItem);

        if (result < 0) {
            if (missingInRight) {
                missingInRight(leftItem);
            }
            leftIndex++;
        } else if (result > 0) {
            if (missingInLeft) {
                missingInLeft(rightItem);
            }
            rightIndex++;
        } else {
            if (matched) {
                matched(leftItem, rightItem);
            }
            leftIndex++;
            rightIndex++;
        }
    }
}

export interface CompareArraysResults<TLeft, TRight> {
    missingInLeft: TRight[];
    missingInRight: TLeft[];
    matched: Array<{ left: TLeft; right: TRight }>;
}

export function compareArraysWithResults<TLeft, TRight>(
    options: CompareArraysOptions<TLeft, TRight>,
): CompareArraysResults<TLeft, TRight> {
    const result: CompareArraysResults<TLeft, TRight> = {
        matched: [],
        missingInLeft: [],
        missingInRight: [],
    };

    compareArrays({
        ...options,
        matched: (left, right) => result.matched.push({ left, right }),
        missingInLeft: item => result.missingInLeft.push(item),
        missingInRight: item => result.missingInRight.push(item),
    });

    return result;
}

export interface IDefaultMap<K, V> extends Map<K, V> {
    get(key: K): V;
}

/**
 * Extending directly from the Map class can cause the error "Constructor Map requires new".
 * This implementation avoids that by just proxying all methods in the Map interface.
 */
export class DefaultMap<K, V> implements Map<K, V> {
    public readonly [Symbol.toStringTag] = 'Map' as const;

    protected readonly map: Map<K, V>;

    public constructor(protected readonly defaultFactory: (key: K) => V, entries?: Array<[K, V]>) {
        if (entries) {
            this.map = new Map(entries);
        } else {
            this.map = new Map<K, V>();
        }
    }

    public get(key: K): V {
        let value = this.map.get(key);
        if (value === undefined) {
            value = this.defaultFactory(key);
            this.map.set(key, value);
        }
        return value;
    }

    public get size(): number {
        return this.map.size;
    }

    public [Symbol.iterator](): IterableIterator<[K, V]> {
        return this.map[Symbol.iterator]();
    }

    public clear(): void {
        return this.map.clear();
    }

    public delete(key: K): boolean {
        return this.map.delete(key);
    }

    public entries(): IterableIterator<[K, V]> {
        return this.map.entries();
    }

    public forEach(callbackfn: (value: V, key: K, map: Map<K, V>) => void, thisArg?: any): void {
        return this.map.forEach(callbackfn, thisArg);
    }

    public has(key: K): boolean {
        return this.map.has(key);
    }

    public keys(): IterableIterator<K> {
        return this.map.keys();
    }

    public set(key: K, value: V): this {
        this.map.set(key, value);
        return this;
    }

    public values(): IterableIterator<V> {
        return this.map.values();
    }
}
